import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "../lib/api";
import { AdminHead } from "./AdminLayout";

const blank = { artist: "", track: "", embedUrl: "", year: "" };

export default function CreditsAdmin() {
  const [credits, setCredits] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [sp, setSp] = useState("");  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const dragId = useRef(null);

  const load = () => api.get("/credits").then((r) => setCredits(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSp(r.data?.appleMusicUrl || "")).catch(() => {});
  }, []);

  const saveSp = async () => {
    setMsg("");
    try {
      const { data } = await api.get("/settings");
      await api.put("/settings", { ...data, appleMusicUrl: sp });
      setMsg("Playlist URL saved.");
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      if (editing) await api.put(`/admin/credits/${editing}`, form);
      else await api.post("/admin/credits", form);
      setForm(blank);
      setEditing(null);
      load();
    } catch (e2) {
      setErr(errMsg(e2));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this credit?")) return;
    await api.delete(`/admin/credits/${id}`);
    load();
  };

  const onDrop = async (targetId) => {
    const from = credits.findIndex((c) => c.id === dragId.current);
    const to = credits.findIndex((c) => c.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    // credits are ordered by year, so reordering only breaks ties within a year
    const next = [...credits];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setCredits(next);
    await api.put("/admin/credits/reorder", { ids: next.map((c) => c.id) });
  };

  return (
    <div data-testid="admin-credits">
      <AdminHead title="Credits" sub={`${credits.length} credits · listed newest to oldest by year`} />

      <div className="card" style={{ marginBottom: 40 }}>
        <p className="label" style={{ marginBottom: 12 }}>Apple Music playlist URL (site-wide)</p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            className="inp"
            style={{ flex: 1, minWidth: 240 }}
            value={sp}
            onChange={(e) => setSp(e.target.value)}
            placeholder="https://music.apple.com/us/playlist/..."
            data-testid="apple-music-url-input"
          />
          <button className="btn btn-sm" onClick={saveSp} data-testid="apple-music-save">
            Save
          </button>
        </div>
        {msg && <p className="mono" style={{ fontSize: 13, marginTop: 10 }}>{msg}</p>}
      </div>

      <form className="card" style={{ marginBottom: 40 }} onSubmit={save} data-testid="credit-form">
        <p className="label" style={{ marginBottom: 12 }}>{editing ? "Edit credit" : "Add credit"}</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16 }}>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>Artist</span>
            <input
              className="inp"
              required
              value={form.artist}
              onChange={(e) => setForm({ ...form, artist: e.target.value })}
              data-testid="credit-artist"
            />
          </label>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>Album/EP/Track</span>
            <input
              className="inp"
              required
              value={form.track}
              onChange={(e) => setForm({ ...form, track: e.target.value })}
              data-testid="credit-track"
            />
          </label>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>Album/EP/Track URL (optional)</span>
            <input
              className="inp"
              value={form.embedUrl}
              onChange={(e) => setForm({ ...form, embedUrl: e.target.value })}
              data-testid="credit-embed"
            />
          </label>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>Year</span>
            <input
              className="inp"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })}
              data-testid="credit-year"
            />
          </label>
        </div>
        {err && <p className="mono" style={{ color: "var(--orange)", fontSize: 13, marginTop: 12 }}>{err}</p>}
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button className="btn btn-sm" type="submit" data-testid="credit-save">
            {editing ? "Save" : "Add"}
          </button>
          {editing && (
            <button
              className="btn btn-sm btn-ghost"
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(blank);
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {credits.length ? (
        credits.map((c) => (
          <div
            key={c.id}
            className="drag-item"
            draggable
            onDragStart={() => (dragId.current = c.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(c.id)}
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid var(--shadow)",
              padding: "10px 0",
            }}
            data-testid={`credit-row-${c.id}`}
          >
            <span className="mono" style={{ fontSize: 14 }}>
              ⠿ {c.artist} — {c.track} {c.year && <span style={{ opacity: 0.6 }}>({c.year})</span>}
            </span>
            <span style={{ display: "flex", gap: 8 }}>
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  setEditing(c.id);
                  setForm({ artist: c.artist, track: c.track, embedUrl: c.embedUrl || "", year: c.year || "" });
                }}
                data-testid={`credit-edit-${c.id}`}
              >
                Edit
              </button>
              <button className="btn btn-sm" onClick={() => remove(c.id)} data-testid={`credit-delete-${c.id}`}>
                Delete
              </button>
            </span>
          </div>
        ))
      ) : (
        <div className="empty">— no credits yet —</div>
      )}
    </div>
  );
}
